import { cn } from '@resolute/ui';
import { type ReactNode } from 'react';


/** A labelled range slider with a live-formatted value readout. */
export function SliderField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  display,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  display?: (v: number) => string;
  hint?: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-sm font-medium">{label}</label>
        <span className="text-sm font-semibold tabular-nums text-primary">
          {display ? display(value) : value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
        aria-label={label}
      />
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** A single result metric. `emphasis` renders the hero number for the tool. */
export function ResultStat({
  label,
  value,
  emphasis = false,
  tone = 'primary',
}: {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  tone?: 'primary' | 'success' | 'foreground';
}) {
  const toneClass =
    tone === 'success' ? 'text-success' : tone === 'foreground' ? 'text-foreground' : 'text-primary';
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn('mt-1 font-semibold tabular-nums', emphasis ? 'text-3xl' : 'text-xl', toneClass)}>
        {value}
      </dd>
    </div>
  );
}

/** Two-column tool scaffold: input controls on the left, results on the right. */
export function CalcLayout({ controls, results }: { controls: ReactNode; results: ReactNode }) {
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
      <div className="flex flex-col gap-6 rounded-xl border border-border bg-card p-6">{controls}</div>
      <div className="flex flex-col gap-6 rounded-xl border border-border bg-accent/30 p-6">
        {results}
      </div>
    </div>
  );
}

export function ToolDisclaimer({ children }: { children?: ReactNode }) {
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      {children ??
        'Indicative estimates for illustration only — not a valuation, offer, or financial advice.'}
    </p>
  );
}
